/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Supported industries for onboarding and solution pages.
 * Removed 2025: architecture, civil_engineering, commercial_construction,
 * industrial_construction, engineering_services, project_management,
 * facilities_management, custom (no longer in nav or onboarding).
 */

import type { LucideIcon } from "lucide-react";
import {
  HardHat,
  Hammer,
  Home,
  Plug,
  Settings,
  Sprout,
  Wrench,
} from "lucide-react";

export const INDUSTRY_TYPES = {
  GENERAL_CONTRACTOR: "general_contractor",
  RESIDENTIAL_CONSTRUCTION: "residential_construction",
  ELECTRICAL_CONTRACTOR: "electrical_contractor",
  PLUMBING_CONTRACTOR: "plumbing_contractor",
  HVAC_CONTRACTOR: "hvac_contractor",
  ROOFING_CONTRACTOR: "roofing_contractor",
  CONCRETE_CONTRACTOR: "concrete_contractor",
  LANDSCAPING: "landscaping",
} as const;

export type IndustryType = (typeof INDUSTRY_TYPES)[keyof typeof INDUSTRY_TYPES];

export interface IndustryDefinition {
  value: IndustryType;
  label: string;
  description: string;
  icon: LucideIcon;
  group: "construction" | "trade" | "services" | "other";
}

export const INDUSTRY_DEFINITIONS: IndustryDefinition[] = [
  {
    value: INDUSTRY_TYPES.GENERAL_CONTRACTOR,
    label: "General Contractor",
    description: "Full-service construction firms managing multiple trades.",
    icon: HardHat,
    group: "construction",
  },
  {
    value: INDUSTRY_TYPES.RESIDENTIAL_CONSTRUCTION,
    label: "Residential Construction",
    description: "Home builders, remodelers, and housing specialists.",
    icon: Home,
    group: "construction",
  },
  {
    value: INDUSTRY_TYPES.ELECTRICAL_CONTRACTOR,
    label: "Electrical Contractor",
    description: "Low-voltage, high-voltage, and systems integrators.",
    icon: Plug,
    group: "trade",
  },
  {
    value: INDUSTRY_TYPES.PLUMBING_CONTRACTOR,
    label: "Plumbing Contractor",
    description: "Commercial and residential plumbing contractors.",
    icon: Wrench,
    group: "trade",
  },
  {
    value: INDUSTRY_TYPES.HVAC_CONTRACTOR,
    label: "HVAC Contractor",
    description: "Heating, ventilation, air conditioning, and refrigeration.",
    icon: Settings,
    group: "trade",
  },
  {
    value: INDUSTRY_TYPES.ROOFING_CONTRACTOR,
    label: "Roofing Contractor",
    description: "Roofing, waterproofing, solar, and exterior specialists.",
    icon: Hammer,
    group: "trade",
  },
  {
    value: INDUSTRY_TYPES.CONCRETE_CONTRACTOR,
    label: "Concrete Contractor",
    description: "Structural concrete, flatwork, and foundations.",
    icon: Hammer,
    group: "trade",
  },
  {
    value: INDUSTRY_TYPES.LANDSCAPING,
    label: "Landscaping & Site Work",
    description: "Landscape architecture, design-build, and maintenance.",
    icon: Sprout,
    group: "trade",
  },
];

export const DEFAULT_INDUSTRY_TYPE: IndustryType =
  INDUSTRY_TYPES.GENERAL_CONTRACTOR;

export function findIndustryDefinition(
  industry: IndustryType,
): IndustryDefinition | undefined {
  return INDUSTRY_DEFINITIONS.find(
    (definition) => definition.value === industry,
  );
}
